import React, { useState } from "react";
import {
  LinearProgress,
  Typography,
  Box,
  IconButton,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { SnackbarType, useSnackbarStore } from "../../stores/useSnackbarStore";
import UploadIcon from "@mui/icons-material/Upload";
import { personAssetsService } from "../../services/personFinancialService";
import { useMimshakStore } from "../../stores/mimshakStore";
import { NotificationType, useNotificationStore } from "../../stores/notificationStore";
import { useAddUsersToCache } from "../../hooks/useUser";
import { useIdStore, useViewedTypeStore } from "../../stores/selectedToViewStore";
import { useGlobalMimshakStore } from "../../stores/useGlobalMimshakStore";
import { PreviewSaveSummaryDTO, ConfirmedZipInputDTO, UploadedFileType } from "../../interfaces/DTO/mimshakDataFileUpload";
import { CobolConfirmationDialog } from "./Dialogs/CobolConfirmationDialog";
import { AylonConfirmationDialog } from "./Dialogs/AylonConfirmationDialog";


const Dropzone = styled(Paper)(({ theme }) => ({
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0, 4),
  textAlign: "center",
  backgroundColor: theme.palette.background.default,
  cursor: "pointer",
  height: 40,
  display: "flex",
  alignItems: "center",
  "&.drag-over": {
    backgroundColor: theme.palette.uploadFile.dragOver,
  },
}));

interface MiniUploadFileProps {
  onFileUploaded?: (file: File) => void;
  isPreAdvice?: boolean;
}

export const MiniUploadFile: React.FC<MiniUploadFileProps> = ({ onFileUploaded, isPreAdvice }) => {
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [confirmationZipName, setConfirmationZipName] = useState<string | null>(null);
  const [confirmationFile, setConfirmationFile] = useState<File | null>(null);
  const [confirmationHandlerCode, setConfirmationHandlerCode] = useState<string | null>(null);
  const [confirmationProposedDate, setConfirmationProposedDate] = useState<string | null>(null);
  const [confirmationFileType, setConfirmationFileType] = useState<UploadedFileType | null>(null);

  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
  const addOrUpdateRecord = useMimshakStore((state) => state.addOrUpdateRecord);
  const addNotification = useNotificationStore((state) => state.addNotification);
  const setNotificationsType = useNotificationStore((state) => state.setNotificationsState);
  const addUsersToCache = useAddUsersToCache();
  const setId = useIdStore((state) => state.setId);
  const setViewedType = useViewedTypeStore((state) => state.setViewedType);

  const MAX_CONCURRENT_UPLOADS = 2;

  const handleFilesUpload = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const queue = [...fileArray];
    const activeUploads: Promise<void>[] = [];

    const processNext = async () => {
      if (queue.length === 0) return;

      const file = queue.shift();
      if (file) {
        const uploadPromise = handleAction(file).finally(() => {
          activeUploads.splice(activeUploads.indexOf(uploadPromise), 1);
          processNext();
        });

        activeUploads.push(uploadPromise);

        if (activeUploads.length < MAX_CONCURRENT_UPLOADS) {
          processNext();
        }
      }
    };

    for (let i = 0; i < Math.min(MAX_CONCURRENT_UPLOADS, fileArray.length); i++) {
      processNext();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      handleFilesUpload(event.target.files);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handleFilesUpload(event.dataTransfer.files);
    }
  };

  const [isDragOver, setIsDragOver] = useState<boolean>(false);

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleAction = async (file: File, handlerCode?: string, confirmedReportDate?: Date) => {
  try {
    setIsUploading(true);

    const response: PreviewSaveSummaryDTO = await personAssetsService.previewFileAndSaveAll(
      file,
      handlerCode,
      confirmedReportDate,
      isPreAdvice
    );

    // 🔍 חיפוש תוצאה שדורשת אישור מהמשתמש
    const awaitingConfirmationResult = response.preview_results?.find(
      (r) =>
        r.status === "awaiting_user_confirmation" &&
        (r.file_type === UploadedFileType.FINANCIAL_POLICIES_COBOL ||
          r.file_type === UploadedFileType.AYLON_PRODUCTION_FILES)
    );

    if (awaitingConfirmationResult) {
      setConfirmationDialogOpen(true);
      setConfirmationZipName(file.name);
      setConfirmationFile(file);
      setConfirmationFileType(awaitingConfirmationResult.file_type ?? null);
      const awaitingResultProposedData = response.preview_results?.find(
        (r) =>
          r.status === "awaiting_user_confirmation" &&
          (r.proposed_handler_code || r.proposed_report_date)
      );
      // אופציות לקוד מטפל – גם מהתוצאה וגם fallback
      setConfirmationHandlerCode(awaitingResultProposedData.proposed_handler_code ?? null);

      // תאריך מוצע
      setConfirmationProposedDate(awaitingResultProposedData.proposed_report_date ?? null);
      return;
    }

    if (response.success_contacts_count === 1 && response.mimshak_data_sample) {
      setViewedType("USER");
      setId(response.mimshak_data_sample.contact_id);
    }

    // איסוף פוליסות שנכשלו מכל התוצאות
    const allFailedPolicies = response.preview_results?.flatMap(
      (result) => result.failed_policies || []
    ) || [];

    if ((response.failed_files_count ?? 0) > 0 || allFailedPolicies.length > 0) {
      setNotificationsType(NotificationType.ERROR);
      showSnackbar("העלאת קובץ לא הצליחה במלואה", SnackbarType.ERROR);
    } else {
      showSnackbar("העלאת קובץ הצליחה", SnackbarType.SUCCESS);
    }

    if (onFileUploaded) {
      onFileUploaded(file);
    }

    // הוספת פוליסות שנכשלו להודעה
    let notificationMessage = `קובץ: ${file.name} | הצליחו: ${response.successful_files_count ?? 0} | נכשלו: ${response.failed_files_count ?? 0}`;

    if (allFailedPolicies.length > 0) {
      notificationMessage += ` | פוליסות שנכשלו: ${allFailedPolicies.length}`;
      allFailedPolicies.forEach(([policyNumber, error]) => {
        sendNotification(`פוליסה ${policyNumber}: ${error}`);
      });
    }

    sendNotification(notificationMessage);
  } catch (error: any) {
    setNotificationsType(NotificationType.ERROR);
    sendNotification(`${error.message} קובץ: ${file.name} טעינה נכשלה`);
    showSnackbar("העלאת קובץ נכשלה", SnackbarType.ERROR);
  } finally {
    setIsUploading(false);
  }
};


 const handleConfirmationSubmit = async ({
    confirmedDate,
    handlerCode,
  }: { confirmedDate: Date; handlerCode: string }) => {
    if (!confirmationFile) return;
    await handleAction(confirmationFile, handlerCode, confirmedDate);
    setConfirmationDialogOpen(false);
  };

  // איילון: קוד המטפל קבוע (מגיע מה-BE כ-proposed_handler_code) - אין בחירת יצרן ב-UI
  const handleAylonConfirmationSubmit = async ({
    confirmedDate,
  }: { confirmedDate: Date }) => {
    if (!confirmationFile || !confirmationHandlerCode) return;
    await handleAction(confirmationFile, confirmationHandlerCode, confirmedDate);
    setConfirmationDialogOpen(false);
  };

  const sendNotification = (message: string) => {
    addNotification(message);
  };

  const handleDropzoneClick = () => {
    const fileInput = document.getElementById("file-input") as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  return (
    <Box sx={{ margin: "0 auto", padding: "0 2px", direction: "ltr" }}>
      <Dropzone
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragLeave={handleDragLeave}
        onClick={handleDropzoneClick}
        sx={{
          padding: "0 2px",
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 1
        }}
        className={isDragOver ? "drag-over" : ""}
      >
        <Typography variant="body1" sx={{ fontWeight: "bold",paddingLeft:2 }}>
          זרוק קובץ
        </Typography>
        <input
          type="file"
          style={{ display: "none" }}
          id="file-input"
          multiple
          onChange={handleFileChange}
        />
        <IconButton
          color="primary"
          component="span"
          size="small"
          sx={{ padding: 0, border: "none", backgroundColor: "transparent", "&:hover": { backgroundColor: "transparent" } }}
        >
          <UploadIcon />
        </IconButton>
      </Dropzone>

      {isUploading && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress variant="indeterminate" sx={{ height: 10, borderRadius: 5 }} />
        </Box>
      )}

     {confirmationDialogOpen && confirmationZipName && confirmationFileType === UploadedFileType.AYLON_PRODUCTION_FILES && (
      <AylonConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={handleAylonConfirmationSubmit}
        zipFilename={confirmationZipName}
        proposedDate={confirmationProposedDate || undefined}
        handlerCode={confirmationHandlerCode || ""}
      />
    )}

     {confirmationDialogOpen && confirmationZipName && confirmationFileType !== UploadedFileType.AYLON_PRODUCTION_FILES && (
      <CobolConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={handleConfirmationSubmit}
        zipFilename={confirmationZipName}
        proposedDate={confirmationProposedDate || undefined}
        proposedHandlerCode={confirmationHandlerCode || undefined}  // ✅ חדש
      />
    )}
    </Box>
  );
};